import type { CSSProperties } from 'react';
import type { FeaturedVideo } from '@/shared/types';
import { BookmarkFilledIcon, BookmarkIcon, IconButton, PlayIcon, ValoiV, cx } from '@/shared/ui';
import { formatDuration } from '@/shared/utils/format';
import { cssUrl } from '@/shared/utils/cssUrl';

export type BrowseCardProps = {
  video: FeaturedVideo;
  active: boolean;
  saved: boolean;
  onSelect: (video: FeaturedVideo) => void;
  onToggleSave: (video: FeaturedVideo) => void;
  /**
   * Render the thumbnail as a silent looping clip instead of a still.
   *
   * Off by default, and Home leaves it off deliberately. A signed-in viewer is
   * looking for a specific title in a library they already know, and a grid of
   * competing motion makes that harder, not easier. A first-time visitor is
   * deciding whether any of this is worth an account, and there the moving grid
   * IS the argument -- it shows what the product makes without asking for a
   * click. Same component, because these are two audiences for one catalogue,
   * not two catalogues.
   *
   * The cost is real: every card holds an open video connection. The landing page
   * shows a handful; a full library would not want this at any size.
   */
  motion?: boolean;
};

/**
 * A tile in a browse rail. Four states in the Figma `Card tile - Search` set:
 * default, hover (bookmark + Add to Library), saved, and saved-hover
 * (Remove from Library).
 */
export function BrowseCard({
  video,
  active,
  saved,
  onSelect,
  onToggleSave,
  motion = false,
}: BrowseCardProps) {
  const duration = formatDuration(video.durationSeconds);
  const meta = video.tags?.slice(0, 2).join(' · ');

  return (
    <article className={cx('browse-card', active && 'is-active')}>
      {/* An explicit name, or the accessible name becomes the concatenation of
          the duration badge, the title and the tags — "2:00 The Tortoise and the
          Hare fable" is not what a screen reader should announce for this. */}
      <button
        type="button"
        className="browse-card__hit"
        aria-label={video.title}
        aria-pressed={active}
        onClick={() => onSelect(video)}
      >
        <span
          className="browse-card__thumb"
          /* Feeds the reduced-motion rule, which hides the <video> and paints
             this instead. CSS cannot read a `poster` attribute, and the URL is
             per-entry so it cannot live in the stylesheet. */
          style={
            motion && video.thumbnailUrl
              ? ({ '--card-poster': cssUrl(video.thumbnailUrl) } as CSSProperties)
              : undefined
          }
        >
          {motion && video.videoUrl ? (
            /* The poster still carries the first frame, so the card is never
               empty while the clip buffers -- and it is what the reduced-motion
               rule paints once the stylesheet hides the video. `preload=metadata`
               rather than `auto`: a rail of cards preloading whole clips is
               megabytes of video nobody asked for. */
            <video
              src={video.videoUrl}
              poster={video.thumbnailUrl}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              tabIndex={-1}
              aria-hidden
            />
          ) : video.thumbnailUrl ? (
            <img src={video.thumbnailUrl} alt="" loading="lazy" />
          ) : (
            <span className="browse-card__placeholder" aria-hidden />
          )}
          {/* No play badge over a clip that is already moving -- it would be
              offering to start something that started on its own. */}
          {motion && video.videoUrl ? null : (
            <span className="browse-card__play" aria-hidden>
              <PlayIcon size={18} />
            </span>
          )}
          {/* Attribution on the frame, the way the reference marks its own reel. CSS
              over the player rather than burned into the file: it costs nothing, it
              applies to every video including the ones already rendered, and it is
              honest about what it is -- a label on our page, not a claim that the
              file itself is marked. A downloaded copy carries no mark. */}
          {motion && video.videoUrl ? (
            <span className="browse-card__watermark" aria-hidden>
              <ValoiV size={13} />
              Valoi
            </span>
          ) : null}
          {duration ? <span className="browse-card__duration">{duration}</span> : null}
          {active ? <span className="browse-card__now">Now playing</span> : null}
        </span>

        <span className="browse-card__title">{video.title}</span>
        {meta ? <span className="browse-card__meta">{meta}</span> : null}
      </button>

      {/* Outside the hit area: saving must not also change what is playing. */}
      <div className="browse-card__save">
        <IconButton
          label={saved ? `Remove ${video.title} from library` : `Add ${video.title} to library`}
          size="sm"
          variant="secondary"
          onClick={() => onToggleSave(video)}
        >
          {saved ? <BookmarkFilledIcon size={15} /> : <BookmarkIcon size={15} />}
        </IconButton>
      </div>
    </article>
  );
}
